import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { ResultsTable, CALC_PRINT_CSS } from "@/components/calc/ResultsTable";
import { ExportBar } from "@/components/calc/ExportBar";
import { CalcHistory } from "@/components/calc/CalcHistory";
import { recordCalculation } from "@/lib/calc/history";
import { stepsToText } from "@/lib/calc/steps";
import { tableToCsv, type TableColumn } from "@/lib/calc/table";
import { parseNumber } from "./numberInput";
import { WHO_CLASSES, bmiOf, bmiPrime, bmiSteps, ponderalIndex, roundBmi, targetWeight, whoClass, type WhoClass } from "./bmi";

/**
 * Under the BMI result: BMI Prime and Ponderal Index, the WHO table with the person's row marked, formula and worked example,
 * target weight, and the gated export. Adult figures only (the page hides these for under 20). Loaded on demand (React.lazy).
 */
export default function BmiExtras({ kg, cm, imperial, gate }: { kg: number; cm: number; imperial: boolean; gate: () => Promise<boolean> }) {
  const [target, setTarget] = useState("24.9");
  const [mode, setMode] = useState<"bmi" | "prime">("bmi");
  const shown = roundBmi(bmiOf(kg, cm)), who = whoClass(shown);
  const w = (n: number) => (imperial ? `${(n / 0.45359237).toFixed(0)} lb` : `${n.toFixed(1)} kg`);
  const steps = useMemo(() => bmiSteps(kg, cm, w), [kg, cm, imperial]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols: TableColumn<WhoClass>[] = [
    { key: "label", header: "WHO class", get: (c) => c.label, format: (c) => `${c.label}${c.id === who.id ? " (you)" : ""}`, align: "left" },
    { key: "range", header: "BMI (kg/m2)", get: (c) => c.range, align: "left" },
  ];
  const t = target.trim() === "" ? null : parseNumber(target);
  const tBmi = t === null ? null : mode === "prime" ? t * 25 : t;
  const tw = tBmi === null ? null : targetWeight(kg, cm, tBmi);

  const summary = `BMI ${shown.toFixed(1)}, Prime ${bmiPrime(bmiOf(kg, cm)).toFixed(2)}, Ponderal Index ${ponderalIndex(kg, cm).toFixed(1)}, ${who.label}`;
  useEffect(() => {
    const id = setTimeout(() => recordCalculation("bmi-calculator", summary), 800);
    return () => clearTimeout(id);
  }, [summary]);
  const text = ["BMI calculation", summary, "", "How this was calculated", stepsToText(steps), "", "WHO adult classes", ...WHO_CLASSES.map((c) => `${c.label}: ${c.range}${c.id === who.id ? "  <- you" : ""}`),
    "", "BMI is a screening number, not a diagnosis. Calculated with FreeDailyPro.com"].join("\n");

  return (
    <div className="space-y-6 calc-print-area mt-6" data-testid="bmi-extras">
      <style>{CALC_PRINT_CSS}</style>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center"><p className="text-xs text-muted-foreground">BMI Prime (BMI / 25)</p><p className="text-xl font-bold" data-testid="bmi-prime">{bmiPrime(bmiOf(kg, cm)).toFixed(2)}</p><p className="text-xs text-muted-foreground">1.00 is the top of the normal range</p></div>
        <div className="p-3 rounded-lg border bg-card text-center"><p className="text-xs text-muted-foreground">Ponderal Index (kg/m3)</p><p className="text-xl font-bold" data-testid="bmi-pi">{ponderalIndex(kg, cm).toFixed(1)}</p><p className="text-xs text-muted-foreground">Weight against height cubed</p></div>
      </div>

      <section aria-label="WHO BMI classes" className="space-y-2">
        <h3 className="text-base font-semibold">WHO adult BMI classes</h3>
        <div data-testid="bmi-who"><ResultsTable caption="WHO adult BMI classification with your class marked" rows={WHO_CLASSES} columns={cols} rowKey={(c) => c.id} maxHeightClass="max-h-96" /></div>
        <p className="text-xs text-muted-foreground">You are in: <strong data-testid="bmi-who-you">{who.label}</strong>. Source: WHO adult BMI classification (WHO Technical Report Series 894, 2000). The Asian cut-offs option above changes the main category but not this table.</p>
      </section>

      <section aria-label="Target weight" className="space-y-2">
        <h3 className="text-base font-semibold">Target weight</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label htmlFor="bmi-target">Target {mode === "prime" ? "BMI Prime" : "BMI"}</Label><Input id="bmi-target" className="w-28" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} /></div>
          <select className="border rounded px-2 py-2 bg-background text-sm" aria-label="Target type" value={mode} onChange={(e) => { setMode(e.target.value as "bmi" | "prime"); setTarget(e.target.value === "prime" ? "1" : "24.9"); }}><option value="bmi">BMI</option><option value="prime">BMI Prime</option></select>
        </div>
        {tw && (tw.ok
          ? <p className="text-sm" data-testid="bmi-target-result">A BMI of {tBmi!.toFixed(1)} at your height is <strong>{w(tw.targetKg)}</strong>, which is {Math.abs(tw.deltaKg) < 0.05 ? "your current weight" : <>{tw.deltaKg < 0 ? "lose" : "gain"} <strong>{w(Math.abs(tw.deltaKg))}</strong></>}.</p>
          : <p role="alert" className="text-sm text-destructive" data-testid="bmi-target-error">{tw.message}</p>)}
        {t !== null && tBmi !== null && !tw && null}
      </section>

      <CalcSteps steps={steps} title="Formula and worked example" />
      <ExportBar gate={gate} text={text} csv={tableToCsv(WHO_CLASSES, [{ key: "label", header: "WHO class", get: (c: WhoClass) => c.label }, { key: "range", header: "BMI (kg/m2)", get: (c: WhoClass) => c.range }, { key: "you", header: "Your class", get: (c: WhoClass) => (c.id === who.id ? "yes" : "") }])} filename="bmi-who-classes.csv" label="result" />
      <CalcHistory tool="bmi-calculator" />
    </div>
  );
}
